import { API_BASE_URL, requestJson } from "../api/apiClient";

const BASE = "/api/admin/weather/radar";

export async function runBartDiscovery() {
  return requestJson(`${BASE}/bart/discover`, { method: "POST" });
}

export async function runBartCacheSync() {
  return requestJson(`${BASE}/bart/sync`, { method: "POST" });
}

export async function runBartCacheCleanup() {
  return requestJson(`${BASE}/bart/cleanup`, { method: "POST" });
}

export async function fetchBartLatestFrame() {
  return requestJson(`${BASE}/bart/latest-frame`);
}

export async function fetchBartCalibration() {
  return requestJson(`${BASE}/bart/calibration`);
}

export async function fetchRadarDiagnostics({ sourceMode = "preferred", refresh = false } = {}) {
  const params = new URLSearchParams({ sourceMode });
  if (refresh) {
    params.set("refresh", "true");
  }

  return requestJson(`${BASE}/diagnostics?${params.toString()}`);
}

export async function runSiataDiscovery() {
  return requestJson(`${BASE}/siata-santa-elena/discover`, { method: "POST" });
}

export async function runSiataCacheSync() {
  return requestJson(`${BASE}/siata-santa-elena/sync`, { method: "POST" });
}

export async function runSiataCacheCleanup() {
  return requestJson(`${BASE}/siata-santa-elena/cleanup`, { method: "POST" });
}

export async function runAerocivilDiscovery() {
  return requestJson(`${BASE}/aerocivil/discover`, { method: "POST" });
}

export async function runAerocivilCacheSync() {
  return requestJson(`${BASE}/aerocivil/sync`, { method: "POST" });
}

export async function fetchAerocivilLatestFrame() {
  return requestJson(`${BASE}/aerocivil/latest-frame`);
}

export async function fetchAerocivilDiagnostics({ refresh = false } = {}) {
  const params = new URLSearchParams();
  if (refresh) {
    params.set("refresh", "true");
  }
  const query = params.toString();
  return requestJson(`${BASE}/aerocivil/diagnostics${query ? `?${query}` : ""}`);
}

export async function fetchAerocivilRadarHealth({ refresh = false } = {}) {
  const params = new URLSearchParams();
  if (refresh) {
    params.set("refresh", "true");
  }
  const query = params.toString();
  const response = await fetch(
    `${API_BASE_URL}/api/weather/radar/aerocivil/health${query ? `?${query}` : ""}`,
    { credentials: "include" }
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message || `Aerocivil health request failed (${response.status})`);
  }

  return response.json();
}

export async function fetchAerocivilGeoref() {
  return requestJson(`${BASE}/aerocivil/georef`);
}

export async function saveAerocivilCalibration({ bounds, site, notes, boundsConfirmed }) {
  return requestJson(`${BASE}/aerocivil/calibration`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bounds, site, notes, boundsConfirmed }),
  });
}

export async function saveAerocivilOutreachStatus({ status, notes, sentAt, respondedAt }) {
  return requestJson(`${BASE}/aerocivil/outreach-status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, notes, sentAt, respondedAt }),
  });
}

export async function saveAerocivilProductionSignoff({
  signedOff,
  boundsConfirmed,
  manualQaComplete,
  notes,
  checklist,
}) {
  return requestJson(`${BASE}/aerocivil/production-signoff`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ signedOff, boundsConfirmed, manualQaComplete, notes, checklist }),
  });
}

export async function fetchSiataLatestFrame() {
  return requestJson(`${BASE}/siata-santa-elena/latest-frame`);
}

export async function fetchSiataGeoref() {
  return requestJson(`${BASE}/siata-santa-elena/georef`);
}

export async function saveSiataCalibration({ bounds, notes, boundsConfirmed }) {
  return requestJson(`${BASE}/siata-santa-elena/calibration`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bounds, notes, boundsConfirmed }),
  });
}

export async function fetchSourceModeDiagnostics({
  sourceMode = "valley_preferred",
  refresh = false,
} = {}) {
  const params = new URLSearchParams({ sourceMode });
  if (refresh) {
    params.set("refresh", "true");
  }
  return requestJson(`${BASE}/source-mode/diagnostics?${params.toString()}`);
}

export async function fetchSiataDiagnostics({ sourceMode = "valley_preferred", refresh = false } = {}) {
  const params = new URLSearchParams({ sourceMode });
  if (refresh) {
    params.set("refresh", "true");
  }
  return requestJson(`${BASE}/siata-santa-elena/diagnostics?${params.toString()}`);
}

export async function exportSiataDiagnostics({ sourceMode = "valley_preferred", refresh = false } = {}) {
  const params = new URLSearchParams({ sourceMode });
  if (refresh) {
    params.set("refresh", "true");
  }

  const response = await fetch(
    `${API_BASE_URL}${BASE}/siata-santa-elena/diagnostics/export?${params.toString()}`,
    { credentials: "include" }
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message || `SIATA diagnostics export failed (${response.status})`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] || `siata-santa-elena-radar-diagnostics-${Date.now()}.json`;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);

  return { ok: true, filename };
}

export async function exportRadarDiagnostics({ sourceMode = "preferred", refresh = false } = {}) {
  const params = new URLSearchParams({ sourceMode });
  if (refresh) {
    params.set("refresh", "true");
  }

  const response = await fetch(`${API_BASE_URL}${BASE}/diagnostics/export?${params.toString()}`, {
    credentials: "include",
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message || `Diagnostics export failed (${response.status})`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] || `weather-radar-diagnostics-${Date.now()}.json`;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);

  return { ok: true, filename };
}
